import { create } from 'zustand';
import { authApi } from '../api/auth';
import { User, AuthState, LoginResponse } from '../types';
import { getRolePermissions } from '../utils/permissions';

declare global {
  interface Window {
    OneSignal?: any;
  }
}

interface AuthStore extends AuthState {
  // Actions
  init: () => void;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  fetchMe: () => Promise<{ success: boolean; user?: User; error?: string }>;
  updateUser: (user: User) => void;
  
  // Computed properties
  permissions: ReturnType<typeof getRolePermissions>;
  hasPermission: (permission: keyof ReturnType<typeof getRolePermissions>) => boolean;
}

const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: getRolePermissions('visor_reportes'), // Default permissions

  // Initialize from localStorage
  init: () => {
    const token = localStorage.getItem('jwt_token');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        const permissions = getRolePermissions(user.role);
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
          permissions,
        });
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
        set({ isLoading: false });
      }
    } else {
      set({ isLoading: false });
    }
  },

  // Login
  login: async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      console.log('Login response:', response);
      
      // Handle different response formats
      const token = response.token || response.data?.token;
      const user = response.user || response.data?.user;
      
      if (!token || !user) {
        throw new Error('Respuesta de login inválida');
      }
      
      console.log('User object:', user);
      
      localStorage.setItem('jwt_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      const permissions = getRolePermissions(user.role);
      
      set({
        user,
        token,
        isAuthenticated: true,
        permissions,
      });
      
      // Register OneSignal userId after successful login
      // Wait a bit for OneSignal to be ready
      setTimeout(async () => {
        try {
          if (window.OneSignal) {
            // Try to get userId
            let onesignalUserId: string | null = null;
            
            if (typeof window.OneSignal.getUserId === 'function') {
              try {
                onesignalUserId = await window.OneSignal.getUserId();
              } catch (e) {
                console.log('Could not get OneSignal userId immediately:', e);
                // Try again after a delay
                setTimeout(async () => {
                  try {
                    onesignalUserId = await window.OneSignal.getUserId();
                    if (onesignalUserId) {
                      await authApi.registerOneSignalUserId(onesignalUserId);
                      console.log('OneSignal userId registered:', onesignalUserId);
                    }
                  } catch (err) {
                    console.log('Could not register OneSignal userId:', err);
                  }
                }, 2000);
                return;
              }
            }
            
            if (onesignalUserId) {
              await authApi.registerOneSignalUserId(onesignalUserId);
              console.log('OneSignal userId registered:', onesignalUserId);
            } else {
              console.log('OneSignal userId not available yet');
            }
          }
        } catch (error) {
          console.log('Error registering OneSignal userId:', error);
          // Don't fail login if OneSignal registration fails
        }
      }, 1000);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'Error al iniciar sesión' 
      };
    }
  },

  // Logout
  logout: async () => {
    // Unregister OneSignal userId before logout
    try {
      if (window.OneSignal && typeof window.OneSignal.getUserId === 'function') {
        const onesignalUserId = await window.OneSignal.getUserId();
        if (onesignalUserId) {
          await authApi.unregisterOneSignalUserId(onesignalUserId);
          console.log('OneSignal userId unregistered:', onesignalUserId);
        }
      }
    } catch (error) {
      console.log('Error unregistering OneSignal userId on logout:', error);
      // Don't block logout if OneSignal unregister fails
    }

    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      permissions: getRolePermissions('visor_reportes'),
    });
  },

  // Get current user
  fetchMe: async () => {
    try {
      const response = await authApi.me();
      const user = response.data || response;
      
      if (!user) {
        throw new Error('Usuario no encontrado');
      }
      
      localStorage.setItem('user', JSON.stringify(user));
      const permissions = getRolePermissions(user.role);
      
      set({ 
        user, 
        permissions,
        isAuthenticated: true 
      });
      
      return { success: true, user };
    } catch (error) {
      console.error('Fetch me error:', error);
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user');
      set({ 
        isAuthenticated: false, 
        user: null, 
        token: null,
        permissions: getRolePermissions('visor_reportes'),
      });
      return { success: false, error: error.message };
    }
  },

  // Update user data
  updateUser: (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    const permissions = getRolePermissions(user.role);
    set({ user, permissions });
  },

  // Check permission
  hasPermission: (permission: keyof ReturnType<typeof getRolePermissions>) => {
    const { permissions } = get();
    return permissions[permission];
  },
}));

export default useAuthStore;

